import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CourseManagementState, ICourse, ITrack } from './types';

export const initialState: CourseManagementState = {
  selectedTrack: undefined,
  courseList: [],
  selectedCourse: undefined,
  loading: false,
  actionInProgress: false,
  foundationConceptsLoading: false,
  trackListLoading: false,
  trackDeleting: false,
  reorderTracksLoading: false,
  page: { totalElements: 0, totalPages: 0, size: 10, number: 1 },
  trackList: [],
  trackContents: {},
  courseStatus: true,
  courseStatusLoading: false
};

const courseManagementSlice = createSlice({
  name: 'courseManagement',
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setSelectedTrack: (state, action) => {
      state.selectedTrack = action.payload;
    },

    fetchCourseList: (state, action) => {
      state.loading = true;
      state.selectedCourse = undefined;
      state.courseList = [];
    },
    fetchCourseListSuccess: (state, action) => {
      state.loading = false;
      state.courseList = action.payload.data;
      state.page = action.payload.page;
    },
    fetchCourseListFailure: (state, action) => {
      state.loading = false;
    },
    fetchCourse: (state, action) => {
      state.selectedCourse = undefined;
      state.actionInProgress = true;
    },
    fetchCourseSuccess: (state, action) => {
      state.actionInProgress = false;
      state.selectedCourse = action.payload;
    },
    fetchCourseFailure: (state, action) => {
      state.actionInProgress = false;
    },
    createCourse: (state, action) => {
      state.loading = true;
    },
    createCourseSuccess: (state, action) => {
      state.loading = false;
      state.selectedCourse = action.payload;
    },
    createCourseFailure: (state, action) => {
      state.loading = false;
    },
    updateCourse: (state, action) => {
      state.loading = true;
      // state.selectedCourse = action.payload;
    },
    updateCourseSuccess: (state, action) => {
      state.loading = false;
      state.selectedCourse = action.payload;
    },
    updateCourseFailure: (state, action) => {
      state.loading = false;
    },
    publishCourse: (state, action) => {
      state.actionInProgress = true;
    },
    publishCourseSuccess: (state, action) => {
      state.actionInProgress = false;
      const index = state.courseList.findIndex((course: ICourse) => course.uid === action.payload.uid);
      if (index !== -1) {
        state.courseList[index] = { ...state.courseList[index], ...action.payload };
      }
    },
    publishCourseFailure: (state, action) => {
      state.actionInProgress = false;
    },
    archiveCourse: (state, action) => {
      state.actionInProgress = true;
    },
    archiveCourseSuccess: (state, action) => {
      state.actionInProgress = false;
      const index = state.courseList.findIndex((course: ICourse) => course.uid === action.payload.uid);
      if (index !== -1) {
        state.courseList[index] = { ...state.courseList[index], ...action.payload };
      }
    },
    archiveCourseFailure: (state, action) => {
      state.actionInProgress = false;
    },
    deleteCourse: (state, action) => {
      state.actionInProgress = true;
    },
    deleteCourseSuccess: (state, action) => {
      state.actionInProgress = false;
    },
    deleteCourseFailure: (state, action) => {
      state.actionInProgress = false;
    },
    createTrack: (state, action) => {
      state.actionInProgress = true;
    },
    createTrackSuccess: (state, action) => {
      state.actionInProgress = false;

      const { contents, ...rest } = action.payload;
      state.trackContents[action.payload.uid] = contents;
      state.trackList.push({ ...rest });
    },
    createTrackFailure: (state, action) => {
      state.actionInProgress = false;
    },
    updateTrack: (state, action) => {
      state.actionInProgress = true;
    },
    updateTrackSuccess: (state, action) => {
      state.actionInProgress = false;

      state.trackContents[action.payload.uid] = action.payload.contents;
      const index = state.trackList.findIndex((track: ITrack) => track.uid === action.payload.uid);
      if (index !== -1) {
        const { contents, ...rest } = action.payload;
        state.trackList[index] = { ...state.trackList[index], ...rest };
      }
    },
    updateTrackFailure: (state, action) => {
      state.actionInProgress = false;
    },
    deleteTrack: (state, action) => {
      state.trackDeleting = true;
    },
    deleteTrackSuccess: (state, action) => {
      state.trackList = state.trackList.filter((track: ITrack) => track.uid !== action.payload.trackId);
      state.trackDeleting = false;
    },
    deleteTrackFailure: (state, action) => {
      state.trackDeleting = false;
    },
    getTrack: (state, action) => {
      state.loading = true;
    },
    getTrackSuccess: (state, action) => {
      state.trackContents[action.payload.trackId] = action.payload.contents;
      state.loading = false;
    },
    getTrackFailure: (state, action) => {
      state.loading = false;
    },
    getTracks: (state, action) => {
      state.trackList = [];
      state.trackListLoading = true;
    },
    getTracksSuccess: (state, action) => {
      state.trackListLoading = false;
      state.trackList = action.payload.tracks;
      state.selectedCourse = action.payload.course;
    },
    getTracksFailure: (state, action) => {
      state.trackListLoading = false;
    },
    getTrackConcepts: (state, action) => {
      state.foundationConceptsLoading = true;
    },
    getTrackConceptsSuccess: (state, action) => {
      state.foundationConceptsLoading = false;
    },
    getTrackConceptsFailure: (state, action) => {
      state.foundationConceptsLoading = false;
    },
    reorderTracks: (state, action) => {
      state.reorderTracksLoading = true;
    },
    reorderTracksSuccess: (state, action) => {
      state.reorderTracksLoading = false;
    },
    reorderTracksFailure: (state, action) => {
      state.reorderTracksLoading = false;
    },
    reorderTrackList: (state, action) => {
      const { startIndex, endIndex } = action.payload;
      const [movedItem] = state.trackList.splice(startIndex, 1);
      state.trackList.splice(endIndex, 0, movedItem);
    },
    clearTrackList: state => {
      state.trackList = [];
      state.selectedTrack = undefined;
      state.selectedCourse = undefined;
      state.trackContents = {};
    },

    getCourseStatus: state => {
      state.courseStatusLoading = true;
    },
    getCourseStatusSuccess: (state, action) => {
      state.courseStatusLoading = false;
      state.courseStatus = action.payload.enabled;
    },
    getCourseStatusFailure: (state, action) => {
      state.courseStatusLoading = false;
    },
    toggleCourseStatus: (state, action) => {
      state.courseStatusLoading = true;
    },
    toggleCourseStatusSuccess: (state, action) => {
      state.courseStatusLoading = false;
      state.courseStatus = action.payload.enabled;
    },
    toggleCourseStatusFailure: (state, action) => {
      state.courseStatusLoading = false;
    }
  }
});

export const {
  resetState,
  setSelectedTrack,
  fetchCourseList,
  fetchCourseListSuccess,
  fetchCourseListFailure,
  fetchCourse,
  fetchCourseSuccess,
  fetchCourseFailure,
  createCourse,
  createCourseSuccess,
  createCourseFailure,
  updateCourse,
  updateCourseSuccess,
  updateCourseFailure,
  publishCourse,
  publishCourseSuccess,
  publishCourseFailure,
  archiveCourse,
  archiveCourseSuccess,
  archiveCourseFailure,
  deleteCourse,
  deleteCourseSuccess,
  deleteCourseFailure,
  createTrack,
  createTrackSuccess,
  createTrackFailure,
  updateTrack,
  updateTrackSuccess,
  updateTrackFailure,
  deleteTrack,
  deleteTrackSuccess,
  deleteTrackFailure,
  getTrack,
  getTrackSuccess,
  getTrackFailure,
  getTracks,
  getTracksSuccess,
  getTracksFailure,
  getTrackConcepts,
  getTrackConceptsSuccess,
  getTrackConceptsFailure,
  reorderTracks,
  reorderTracksSuccess,
  reorderTracksFailure,
  reorderTrackList,
  clearTrackList,
  getCourseStatus,
  getCourseStatusSuccess,
  getCourseStatusFailure,
  toggleCourseStatus,
  toggleCourseStatusSuccess,
  toggleCourseStatusFailure
} = courseManagementSlice.actions;

export default courseManagementSlice.reducer;
